import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const norm = (s) => String(s || '').toLocaleLowerCase('tr-TR');

export function ComboInput({ name, value, onChange, suggestions = [], placeholder, required, testId }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrap = useRef(null);
  useEffect(() => { const h = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);
  const items = useMemo(() => {
    const q = norm(value); const seen = new Set();
    return suggestions.filter(x => x?.label && !seen.has(norm(x.label)) && seen.add(norm(x.label))).filter(x => !q || norm(x.label).includes(q)).slice(0, 40);
  }, [suggestions, value]);
  const groups = useMemo(() => items.reduce((acc, x) => { (acc[x.group || ''] ||= []).push(x); return acc; }, {}), [items]);
  const pick = (label) => { onChange({ target: { name, value: label } }); setOpen(false); setHi(-1); };
  const onKey = (e) => {
    if (!open && ['ArrowDown', 'ArrowUp'].includes(e.key)) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && open && hi >= 0) { e.preventDefault(); pick(items[hi].label); }
    else if (e.key === 'Escape') setOpen(false);
  };
  let idx = -1;
  return <div className="combo" ref={wrap}>
    <input name={name} value={value ?? ''} placeholder={placeholder} required={required} autoComplete="off" onChange={(e) => { onChange(e); setOpen(true); setHi(-1); }} onFocus={() => setOpen(true)} onKeyDown={onKey} data-testid={testId} />
    <button type="button" className="combo-toggle" tabIndex={-1} onClick={() => setOpen(!open)} aria-label="Seçenekleri göster" data-testid={`${testId}-toggle`}><ChevronDown size={14} /></button>
    {open && items.length > 0 && <div className="combo-list" data-testid={`${testId}-list`}>
      {Object.entries(groups).map(([g, rows]) => <div key={g || 'default'} className="combo-group">
        {g && <div className="combo-group-title">{g}</div>}
        {rows.map(x => { idx += 1; const i = idx; return <button type="button" key={x.label} className={`combo-item ${i === hi ? 'hi' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(x.label)} onMouseEnter={() => setHi(i)} data-testid={`${testId}-option-${i}`}><span>{x.label}</span>{x.hint && <small>{x.hint}</small>}</button>; })}
      </div>)}
    </div>}
  </div>;
}

export const BANKS_TR = ['Ziraat Bankası', 'Vakıfbank', 'Halkbank', 'İş Bankası', 'Garanti BBVA', 'Yapı Kredi', 'Akbank', 'QNB', 'Denizbank', 'TEB', 'Kuveyt Türk', 'Albaraka', 'Türkiye Finans', 'Şekerbank', 'ING', 'HSBC', 'Fibabanka', 'Odeabank', 'Enpara', 'Papara'];
export const CATEGORIES_TR = ['Kira', 'Fatura', 'Tedarik', 'Sarf', 'Bakım', 'Personel', 'Vergi · SGK', 'Tahsilat', 'Sabit gider', 'Ulaşım', 'Reklam', 'Diğer'];
export const STAFF_TITLES_TR = ['Garson', 'Barista', 'Tezgahtar', 'Kasiyer', 'Mutfak', 'Aşçı', 'Komi', 'Kurye', 'Temizlik', 'Müdür', 'Vardiya amiri'];

export const uniq = (arr) => [...new Set(arr.filter(Boolean))];

export function buildSuggestions(kind, lists) {
  const { wholesalers = [], accounts = [], cards = [], loans = [], notes = [], transactions = [], recurring = [], staff = [] } = lists;
  if (kind === 'payee') {
    const w = wholesalers.map(x => ({ label: x.name, group: 'Toptancılar', hint: x.contact_person || undefined }));
    const prev = uniq(notes.map(x => x.payee)).filter(p => !wholesalers.some(x => x.name === p)).map(label => ({ label, group: 'Önceki lehtarlar' }));
    return [...w, ...prev];
  }
  if (kind === 'bank') {
    const used = uniq([...accounts, ...cards, ...loans, ...notes].map(x => x.bank)).map(label => ({ label, group: 'Kullandıklarınız' }));
    return [...used, ...BANKS_TR.filter(b => !used.some(u => u.label === b)).map(label => ({ label, group: 'Bankalar' }))];
  }
  if (kind === 'category') {
    const used = uniq([...transactions, ...recurring].map(x => x.category)).map(label => ({ label, group: 'Kullandıklarınız' }));
    return [...used, ...CATEGORIES_TR.filter(c => !used.some(u => u.label === c)).map(label => ({ label, group: 'Öneriler' }))];
  }
  if (kind === 'title') {
    const used = uniq(staff.map(x => x.title)).map(label => ({ label, group: 'Mevcut görevler' }));
    return [...used, ...STAFF_TITLES_TR.filter(c => !used.some(u => u.label === c)).map(label => ({ label, group: 'Öneriler' }))];
  }
  return [];
}
